import React, { useContext, useEffect, useMemo, useState } from 'react';
import Select, { components, StylesConfig } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { ProcessStatus } from 'constants/constants';
import CompanyService from 'services/CompanyService';
import AuthService from 'services/AuthService';
import { CompanyModel } from 'services/EpdClient';
import { EpdLinks } from 'routes/EpdRoutes';
import { Theme } from 'styles/theme';
import { ReactComponent as ChevronDownIcon } from 'images/v2/icons/svg/fill/svg/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from 'images/v2/icons/svg/fill/svg/chevron-up.svg';
import { ReactComponent as CheckIcon } from 'images/v2/icons/svg/fill/svg/check.svg';
import Tooltip from 'components/controls/Tooltip';

interface CompanyOption {
  label: string;
  value: string;
}

const DashboardCompanySelector: React.FC = () => {
  const theme = useTheme() as Theme;
  const history = useHistory();
  const { company, companyAccountId, changeCompany } = useContext(CompanyContext);
  const isAdminUser = AuthService.isAdmin() || AuthService.isLicenseeAdmin();

  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [canRedirect, setCanRedirect] = useState(false);

  const selectedValueRef = React.useRef<HTMLDivElement | null>(null);
  const [isSelectedValueEllipsed, setIsSelectedValueEllipsed] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      setStatus(ProcessStatus.Fetching);

      const allCompanies = await CompanyService.getCompanies();

      // All users see only companies where they have memberships
      const userMemberships = AuthService.getUser()?.memberships?.map((m) => m.companyId) || [];
      const filteredCompanies = allCompanies.filter((c) => userMemberships.includes(c.id as string));

      setCompanies(filteredCompanies);
      setStatus(ProcessStatus.Success);
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (canRedirect) {
      history.push(EpdLinks.dashboard());
    }
  }, [canRedirect, companyAccountId, history]);

  const options: CompanyOption[] = useMemo(() => {
    const companyOptions = companies
      .filter((x) => x.id && x.name)
      .map((x) => ({
        label: x.name ?? '',
        value: x.id ?? '',
      }));

    // Ensure current company is included even if not in filtered list
    if (company?.id && company?.name && !companyOptions.some((opt) => opt.value === company.id)) {
      companyOptions.unshift({
        label: company.name,
        value: company.id,
      });
    }

    return companyOptions;
  }, [companies, company?.id, company?.name]);

  const selectedCompany = useMemo(
    () => options.find((option) => option.value === company?.id) ?? null,
    [options, company?.id]
  );

  useEffect(() => {
    const element = selectedValueRef.current;

    if (!element) {
      setIsSelectedValueEllipsed(false);
      return;
    }

    setIsSelectedValueEllipsed(element.scrollWidth > element.clientWidth);
  }, [selectedCompany?.label]);

  if (status !== ProcessStatus.Success) {
    return null;
  }

  if (isAdminUser || options.length <= 1) {
    // Admins/Licensee Admins: show the company name only, no switcher
    return (
      <Wrapper>
        <SelectedValueMeasure ref={selectedValueRef}>{selectedCompany?.label}</SelectedValueMeasure>
        {isSelectedValueEllipsed && selectedCompany?.label && <Tooltip position="title">{selectedCompany.label}</Tooltip>}
        <StaticCompanyName>{selectedCompany?.label}</StaticCompanyName>
      </Wrapper>
    );
  }

  const handleChange = (selected: CompanyOption | null) => {
    if (!selected?.value || selected.value === company?.id) {
      return;
    }

    changeCompany?.(selected.value);
    setCanRedirect(true);
  };

  const styles: StylesConfig<CompanyOption, false> = {
    control: (base, state) => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      background: 'transparent',
      minHeight: 40,
      cursor: 'pointer',
      fontFamily: "'Inter'",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      color: theme.colors.primaryBlack,
      fontFamily: "'Inter'",
      fontWeight: 700,
      fontSize: 28,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: theme.colors.primaryBlack,
      fontWeight: 700,
      fontSize: 28,
      fontFamily: "'Inter'",
      cursor: 'text',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingLeft: 8,
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: 0,
      color: theme.colors.primaryBlack,
    }),
    menu: (base) => ({
      ...base,
      width: 295,
      marginTop: 4,
      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
      borderRadius: 4,
      overflow: 'hidden',
      zIndex: 10,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: 365,
    }),
    option: (base, state) => ({
      ...base,
      minHeight: 40,
      padding: '11px 16px',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: state.isFocused ? theme.colors.elementsFilledInput : theme.colors.primaryWhite,
      color: theme.colors.primaryBlack,
      fontWeight: state.isSelected ? 600 : 400,
      cursor: state.isSelected ? 'default' : 'pointer',
      fontSize: 14,
      '&:active': {
        backgroundColor: theme.colors.elementsFilledInput,
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 999999,
    }),
    placeholder: (base) => ({
      ...base,
      display: 'none',
    }),
  };

  return (
    <Wrapper>
      <SelectedValueMeasure ref={selectedValueRef}>{selectedCompany?.label}</SelectedValueMeasure>

      {isSelectedValueEllipsed && selectedCompany?.label && <Tooltip position="title">{selectedCompany.label}</Tooltip>}
      <Select<CompanyOption, false>
        options={options}
        value={selectedCompany}
        onChange={handleChange}
        isSearchable
        isClearable={false}
        styles={styles}
        components={{
          DropdownIndicator,
          Option: CompanyOptionComponent,
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </Wrapper>
  );
};

const CompanyOptionComponent = (props: any) => (
  <components.Option {...props}>
    <OptionContent>
      <span>{props.data.label}</span>
      {props.isSelected && <CheckIcon />}
    </OptionContent>
  </components.Option>
);

const DropdownIndicator = (props: any) => {
  const isOpen = props.selectProps.menuIsOpen;

  return (
    <components.DropdownIndicator {...props}>
      {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </components.DropdownIndicator>
  );
};

const SelectedValueMeasure = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;

  max-width: 770px;
  overflow: hidden;
  white-space: nowrap;

  font-family: 'Inter';
  font-weight: 700;
  font-size: 28px;
`;

const Wrapper = styled.div`
  position: relative;
  width: fit-content;
  max-width: 770px;
`;

const OptionContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  svg {
    width: 14px;
    height: 14px;
    color: ${(props) => props.theme.colors.primaryOrange};
    flex-shrink: 0;
    margin-left: 8px;
  }
`;

const StaticCompanyName = styled.div`
  margin: 0;
  color: ${(props) => props.theme.colors.primaryBlack};
  font-family: 'Inter';
  font-weight: 700;
  font-size: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

export default DashboardCompanySelector;
